import { X, ArrowRight, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { SyncPreview } from '../services/api'

interface SyncConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  preview: SyncPreview | null
  isLoading: boolean
  isSyncing?: boolean
  syncError?: string | null
}

export default function SyncConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  preview,
  isLoading,
  isSyncing = false,
  syncError = null,
}: SyncConfirmDialogProps) {
  if (!isOpen) return null

  const renderChangeRow = (
    label: string,
    current: string | number | null | undefined,
    proposed: string | number | null | undefined
  ) => {
    const hasChanged = current !== proposed
    const currentDisplay = current ?? '-'
    const proposedDisplay = proposed ?? '-'

    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center space-x-2">
          <span className={`text-sm ${hasChanged ? 'text-red-600 line-through' : 'text-gray-600'}`}>
            {currentDisplay}
          </span>
          {hasChanged && (
            <>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-green-600">{proposedDisplay}</span>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Confirm Radio Sync</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Sync failure — shown in place so the dialog never just hangs */}
          {syncError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Sync Failed</h3>
                  <p className="text-sm text-red-700 mt-1">{syncError}</p>
                </div>
              </div>
            </div>
          )}
          {isLoading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600">Loading sync preview...</p>
            </div>
          )}

          {!isLoading && preview && (
            <>
              {/* Error state */}
              {preview.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <p className="text-sm text-red-700 mt-1">{preview.error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Not found states */}
              {!preview.radio_exists && !preview.error && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">Radio Not Found</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        Radio {preview.serial_number} was not found in Tarana TCS.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!preview.customer_found && preview.radio_exists && !preview.error && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">No Customer Match</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        Radio {preview.serial_number} is not assigned to any customer in Splynx.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success state - ready to sync */}
              {preview.radio_exists && preview.customer_found && !preview.error && (
                <>
                  {/* Customer info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">Customer Information</h3>
                    <div className="space-y-1 text-sm text-blue-800">
                      <div>
                        <span className="font-medium">Customer:</span> {preview.customer_name}
                      </div>
                      {preview.service_name && (
                        <div>
                          <span className="font-medium">Service:</span> {preview.service_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Changes detected */}
                  {preview.changes_detected ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-medium text-gray-900 mb-3">
                        Proposed Changes
                      </h3>
                      <div className="space-y-1">
                        {renderChangeRow('Hostname', preview.current_hostname, preview.proposed_hostname)}
                        {renderChangeRow('SLA Profile', preview.current_sla, preview.proposed_sla)}
                        {renderChangeRow(
                          'Latitude',
                          preview.current_latitude?.toFixed(6),
                          preview.proposed_latitude?.toFixed(6)
                        )}
                        {renderChangeRow(
                          'Longitude',
                          preview.current_longitude?.toFixed(6),
                          preview.proposed_longitude?.toFixed(6)
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h3 className="text-sm font-medium text-green-800">Already in Sync</h3>
                          <p className="text-sm text-green-700 mt-1">
                            This radio is already synchronized. No changes needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={
              isLoading ||
              isSyncing ||
              !preview ||
              !!preview.error ||
              !preview.radio_exists ||
              !preview.customer_found ||
              !preview.changes_detected
            }
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSyncing ? 'Syncing…' : isLoading ? 'Loading...' : 'Confirm Sync'}
          </button>
        </div>
      </div>
    </div>
  )
}
